import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {AuthService} from '../auth/auth.service';
import {timeout} from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})

export class RequestService {
  endpoint: string;

  constructor(
      private http: HttpClient,
      private authService: AuthService
  ) {
    this.endpoint = environment.endpoint + '/';
  }

  /**
   * Generates headers dynamically.
   * CRITICAL: If data is FormData, we DO NOT set Content-Type.
   */
  private getHeaders(data?: any): HttpHeaders {
    let headers = new HttpHeaders();

    // 1. Add Authorization
    if (!this.authService.isTokenExpired()) {
      const token = this.authService.getToken();
      if (token?.access_token) {
        headers = headers.set('Authorization', `Bearer ${token.access_token}`);
      }
    }

    // 2. Handle Content-Type
    // If data is FormData, the browser MUST set the boundary itself.
    // If we set 'application/json' manually, the multipart upload will fail.
    if (!(data instanceof FormData)) {
      headers = headers.set('Accept', 'application/json');
      // We only set JSON content type if it's not a GET request and not FormData
      if (data) {
        headers = headers.set('Content-Type', 'application/json');
      }
    }

    headers = headers.set('Access-Control-Allow-Origin', '*');
    return headers;
  }

  get(url: string): Observable<any> {
    return this.http.get(this.endpoint + url, { headers: this.getHeaders() });
  }

  post(url: string, data: any, longTimeout = true): Observable<any> {
    return this.http.post(this.endpoint + url, data, { headers: this.getHeaders(data) })
        .pipe(timeout(longTimeout ? 300000 : 60000));
  }

  put(url: string, data: any): Observable<any> {
    return this.http.put(this.endpoint + url, data, { headers: this.getHeaders(data) });
  }

  patch(url: string, data: any): Observable<any> {
    return this.http.patch(this.endpoint + url, data, { headers: this.getHeaders(data) });
  }

  delete(url: string): Observable<any> {
    // Usually delete doesn't have a body, so we don't pass data to getHeaders
    return this.http.delete(this.endpoint + url, { headers: this.getHeaders() });
  }

  // Optimized Upload method for specific configurations
  upload(url: string, data: any, options: any = {}, type: 'GET' | 'POST' = 'POST'): Observable<any> {
    let headers = this.getHeaders(data);

    if (options?.accept) {
      headers = headers.set('Accept', options.accept);
    }

    const httpOptions: any = { headers };
    if (options?.responseType) {
      httpOptions.responseType = options.responseType;
    }

    const request = type === 'GET'
        ? this.http.get(this.endpoint + url, httpOptions)
        : this.http.post(this.endpoint + url, data, httpOptions);

    return request.pipe(timeout(options?.timeoutValue || 24000000));
  }

  makeSocketConnectionUrl(): string {
    return this.endpoint + '/wsMessenger';
  }

  makeSseConnectionUrl(): string {
    return this.endpoint;
  }

  getAnotherDomain(url: string): Observable<any> {
    return this.http.get(url);
  }
}
